import axios from 'axios'
import { getDeviceIdentity } from '@/utils/device'

// Vuex module: device registration + approval gate.
//
// The app registers itself with the backend on launch, before login, and is
// refused entry unless the `devices` row for its uuid has is_active = 1. An
// admin approves devices at /admin/rental/devices/all.

const APPROVED_AT_KEY = 'popin_device_approved_at'

// If the server is unreachable we fall back to the last known-good approval
// rather than locking staff out of a shop-floor app over a flaky connection.
// Beyond this window an offline device must reach the server again.
const OFFLINE_GRACE_DAYS = 7
const OFFLINE_GRACE_MS = OFFLINE_GRACE_DAYS * 24 * 60 * 60 * 1000

function rememberApproval() {
  localStorage.setItem(APPROVED_AT_KEY, String(Date.now()))
}

function withinOfflineGrace() {
  const at = Number(localStorage.getItem(APPROVED_AT_KEY) || 0)
  return at > 0 && Date.now() - at < OFFLINE_GRACE_MS
}

export default {
  namespaced: true,

  state: () => ({
    // idle | checking | approved | pending | blocked | error
    status: 'idle',
    identity: null,     // { uuid, platform, version, manufacturer, model }
    message: '',
    offline: false,     // approved from cache because the server was unreachable
  }),

  getters: {
    // The single question the rest of the app asks.
    isAllowed: (s) => s.status === 'approved',
    isChecking: (s) => s.status === 'checking' || s.status === 'idle',
    uuid: (s) => s.identity?.uuid || '',
    platform: (s) => s.identity?.platform || '',
  },

  mutations: {
    SET_STATUS(state, status) { state.status = status },
    SET_IDENTITY(state, identity) { state.identity = identity },
    SET_MESSAGE(state, message) { state.message = message || '' },
    SET_OFFLINE(state, v) { state.offline = v },
  },

  actions: {
    /**
     * Identify this device, upsert it server-side, and decide whether the app
     * may proceed. Safe to call repeatedly — the endpoint is an upsert.
     */
    async register({ commit }) {
      commit('SET_STATUS', 'checking')
      commit('SET_MESSAGE', '')
      commit('SET_OFFLINE', false)

      let identity
      try {
        identity = await getDeviceIdentity()
        commit('SET_IDENTITY', identity)
      } catch (e) {
        commit('SET_STATUS', 'error')
        commit('SET_MESSAGE', 'Could not read this device’s identity.')
        return 'error'
      }

      try {
        const { data } = await axios.post('/api/device/register', identity)

        if (data?.approved) {
          rememberApproval()
          commit('SET_STATUS', 'approved')
          return 'approved'
        }

        // A device the server has never seen is awaiting approval; one it
        // already knew and still marks inactive has been blocked.
        localStorage.removeItem(APPROVED_AT_KEY)
        commit('SET_STATUS', data?.new ? 'pending' : 'blocked')
        commit('SET_MESSAGE', data?.message)
        return data?.new ? 'pending' : 'blocked'

      } catch (error) {
        const status = error?.response?.status

        // A 4xx/5xx is a real answer from the server, not a connectivity
        // problem, so the offline grace must not apply to it.
        if (status) {
          commit('SET_STATUS', 'error')
          commit('SET_MESSAGE',
            error?.response?.data?.message || `Device check failed (HTTP ${status}).`)
          return 'error'
        }

        if (withinOfflineGrace()) {
          commit('SET_STATUS', 'approved')
          commit('SET_OFFLINE', true)
          return 'approved'
        }

        commit('SET_STATUS', 'error')
        commit('SET_MESSAGE', 'Cannot reach the server to verify this device.')
        return 'error'
      }
    },
  },
}
